/**
 * Manages filtering functionality for the PS Table component.
 *
 * PSTableFiltering handles both user-defined filters and permanent filters, combining
 * them into effective filters that are applied to the table data. It supports
 * different filter join operations (AND/OR) and manages the filter UI visibility.
 *
 * @class PSTableFiltering
 * @example
 * // Create filtering with default settings
 * const filtering = new PSTableFiltering();
 *
 * // Create filtering with custom options
 * const filtering = new PSTableFiltering({
 *   enabled: true,
 *   show_filters: true,
 *   filters: { filter_join: 'OR', filter: [] },
 *   permanent_filters: [{ field: 'status', operator: '=', value: 'active' }]
 * });
 *
 * // Disable filtering completely
 * const filtering = new PSTableFiltering(false);
 */
class PSTableFiltering
{
	/**
	 * Creates a new PSTableFiltering instance.
	 *
	 * @param {Object|boolean} options - Filtering configuration options
	 * @param {boolean} [options.enabled=false] - Whether to show the filtering UI
	 * @param {boolean} [options.show_filters=false] - Whether to show the filter UI in expanded mode. If false, the filter UI will be shown in a collapsed mode.
	 * @param {Object} [options.filters] - User-defined filters with filter_join and filter array
	 * @param {Array|Object} [options.permanent_filters=[]] - Permanent filters that cannot be removed by users. This is used to set the initial filters for the table that persist across page loads.
	 */
	constructor(options)
	{
		// Handle boolean input by converting to object with enabled property
		if (options === false)
			options = { enabled: false };

		options = options ?? {};

		this.enabled = ko_helper.safe_observable(options.enabled, false);
		this.show_filters = ko_helper.safe_observable(options.show_filters, false);
		this.filters = ko_helper.safe_observable(options.filters, { filter_join: 'AND', filter: [] });
		this.permanent_filters = ko_helper.safe_observable(options.permanent_filters, []);

		/**
		 * Computed observable that combines user filters and permanent filters.
		 * Merges the filters based on their presence and returns the effective filter configuration.
		 * @type {ko.PureComputed<Object>}
		 */
		this.effective_filters = ko.pureComputed(() =>
		{
			let effective = {
				filter_join: 'AND',
				filter: []
			};

			let filters = this.filters() ?? { filter_join: 'AND', filter: [] };
			let permanent_filters = this.permanent_filters() ?? [];
			if (permanent_filters instanceof Array)
				permanent_filters = { filter_join: 'AND', filter: permanent_filters };
			else if (permanent_filters instanceof Object)
				permanent_filters = { filter_join: permanent_filters.filter_join ?? 'AND', filter: permanent_filters.filter ?? [] };

			if (filters.filter.length > 0 && permanent_filters.filter.length == 0)
				effective = filters;
			else if (permanent_filters.filter.length > 0 && filters.filter.length == 0)
				effective = permanent_filters;
			else if (permanent_filters.filter.length > 0 && filters.filter.length > 0)
				effective = {
					filter_join: 'AND',
					filter: [filters, permanent_filters]
				};
			else
				effective = {
					filter_join: 'AND',
					filter: []
				};

			return effective;
		});
	}

	/**
	 * Toggles the visibility of the filter UI.
	 */
	toggle_filters()
	{
		this.show_filters(!this.show_filters());
	}

	/**
	 * Updates the user-defined filters with new filter configuration.
	 *
	 * @param {Object} filters - The new filter configuration with filter_join and filter array
	 */
	filters_updated(filters)
	{
		this.filters(filters);
	}
}

export default PSTableFiltering;
